import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import EventCard from "./EventCard";
import AppModalShell from "../common/AppModalShell";
import PhotoRequestModal from "./PhotoRequestModal";
import ConceptDatePollModal from "./ConceptDatePollModal";
import HostEventCompletionModal from "./HostEventCompletionModal";
import { RequiredActionsContext } from "../../context/RequiredActionsContext";
import { useProfileCardModal } from "../../context/ProfileCardContext";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { isNativeApp, openExternalUrl } from "../../native/runtime";
import { getSearchKeyIcon } from "../../constants/searchKeys";
import { prepareEventCheckoutUrl } from "../../utils/eventCheckout";

const HOST_PAID_CANCELLATION_RULE_KEYS = ["rule_1", "rule_2", "rule_3"];

const getNormalizedCancellationPolicy = (rawValue) => {
  const normalized = String(rawValue || "").trim().toLowerCase();
  if (normalized === "flexible" || normalized === "moderate" || normalized === "strict") {
    return normalized;
  }
  return "moderate";
};

const getCancellationPolicyContent = (t, rawPolicy) => {
  const policy = getNormalizedCancellationPolicy(rawPolicy);
  const root = `event_create.step3.payment.host_paid.cancellation_options.${policy}`;
  const rules = HOST_PAID_CANCELLATION_RULE_KEYS.map((ruleKey) => {
    const conditionKey = `${root}.${ruleKey}_condition`;
    const outcomeKey = `${root}.${ruleKey}_outcome`;
    const condition = t(conditionKey);
    const outcome = t(outcomeKey);
    if (condition === conditionKey) return null;
    return {
      key: ruleKey,
      condition,
      outcome: outcome === outcomeKey ? "" : outcome,
    };
  }).filter(Boolean);

  return {
    label: t(`${root}.label`),
    rules,
  };
};

const HostPaidCancellationNotice = ({ t, policy, variant = "remaining" }) => {
  const content = getCancellationPolicyContent(t, policy);

  return (
    <div className="mt-4 rounded-2xl border border-white/12 bg-white/[0.04] px-4 py-4 text-left">
      <div className="text-sm font-semibold uppercase tracking-[0.16em] text-white/55">
        {t("confirm_intent_modal.body.cancellation_policy_title")}
      </div>
      <p className="mt-2 text-sm leading-6 text-white/82">
        {variant === "remaining"
          ? t("confirm_intent_modal.body.cancellation_policy_intro_remaining")
          : t("confirm_intent_modal.body.cancellation_policy_intro_deposit")}
      </p>
      <div className="mt-3 text-base font-semibold text-white">
        {content.label}
      </div>
      <div className="mt-2 space-y-2">
        {content.rules.map((rule) => (
          <div key={rule.key} className="flex items-start justify-between gap-3 text-sm leading-6">
            <span className="text-white/90">{rule.condition}</span>
            {rule.outcome ? (
              <span className="shrink-0 font-medium text-white/72">{rule.outcome}</span>
            ) : null}
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs leading-5 text-white/62">
        {t("confirm_intent_modal.body.host_change_refund_note")}
      </p>
    </div>
  );
};

const splitParagraphs = (...values) =>
  values
    .flatMap((value) => String(value || "").split(/\n\n+/))
    .map((value) => value.trim())
    .filter(Boolean);

const formatDateTime = (isoValue, locale) => {
  if (!isoValue) return "";
  const dt = new Date(isoValue);
  if (Number.isNaN(dt.getTime())) return "";

  return dt.toLocaleString(locale || undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const resolveCheckoutUrl = (payUrl, eventId) => {
  if (typeof window === "undefined") return null;

  const origin = window.location.origin;
  const base = api?.defaults?.baseURL || "";
  let apiOrigin = origin;
  let apiBasePath = "";

  try {
    if (base) {
      const parsedBase = new URL(base, origin);
      apiOrigin = parsedBase.origin;
      apiBasePath = parsedBase.pathname.replace(/\/$/, "");
    }
  } catch {
    // keep origin-only fallback
  }

  let candidate = (typeof payUrl === "string" && payUrl.trim()) || "";
  if (/^https?:\/\//i.test(candidate)) {
    try {
      const parsedCandidate = new URL(candidate);
      if (parsedCandidate.origin !== origin && parsedCandidate.origin !== apiOrigin) {
        return candidate;
      }
      candidate = `${parsedCandidate.pathname}${parsedCandidate.search || ""}`;
    } catch {
      return candidate;
    }
  }

  if (candidate) {
    const path = candidate.startsWith("/") ? candidate : `/${candidate}`;
    if (path.startsWith("/api/")) return `${origin}${path}`;
    return `${apiOrigin}${apiBasePath}${path}`;
  }

  if (!eventId) return null;
  return `${apiOrigin}${apiBasePath}/events/${eventId}/buy/`;
};

const isLikelyInAppBrowser = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator?.userAgent || "";
  const hasTelegramWebApp = Boolean(window.Telegram?.WebApp?.initData);
  return (
    hasTelegramWebApp ||
    /Instagram|FBAN|FBAV|Telegram/i.test(ua)
  );
};

const normalizeRewardAmount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

const formatRewardAmount = (value) => {
  const normalized = normalizeRewardAmount(value);
  if (normalized >= 1e9) {
    const short = (normalized / 1e9).toFixed(1).replace(/\.0$/, "");
    return `${short}B`;
  }
  if (normalized >= 1e6) {
    const short = (normalized / 1e6).toFixed(1).replace(/\.0$/, "");
    return `${short}M`;
  }
  if (normalized >= 1e3) {
    const short = (normalized / 1e3).toFixed(1).replace(/\.0$/, "");
    return `${short}K`;
  }
  return `${normalized}`;
};

const getRewardItems = (rewardPayload = {}, t) => {
  const payload = rewardPayload || {};
  return [
    {
      key: "coins",
      kind: "coins",
      amount: normalizeRewardAmount(payload.coins),
      amountLabel: formatRewardAmount(payload.coins),
      icon: "/assets/vault/coin_icon.svg",
      label: t("participants_section.host_reward_items.coins"),
      iconClassName: "h-7 w-7",
    },
    {
      key: "diamonds",
      kind: "resource",
      amount: normalizeRewardAmount(payload.diamonds),
      amountLabel: formatRewardAmount(payload.diamonds),
      icon: "/assets/vault/diamand_icon.svg",
      label: t("participants_section.host_reward_items.diamonds"),
      iconClassName: "h-7 w-7",
    },
    {
      key: "key1",
      kind: "resource",
      amount: normalizeRewardAmount(payload.keys_level_1),
      amountLabel: formatRewardAmount(payload.keys_level_1),
      icon: getSearchKeyIcon(1),
      label: t("participants_section.host_reward_items.key_level_1"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
    {
      key: "key2",
      kind: "resource",
      amount: normalizeRewardAmount(payload.keys_level_2),
      amountLabel: formatRewardAmount(payload.keys_level_2),
      icon: getSearchKeyIcon(2),
      label: t("participants_section.host_reward_items.key_level_2"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
    {
      key: "key3",
      kind: "resource",
      amount: normalizeRewardAmount(payload.keys_level_3),
      amountLabel: formatRewardAmount(payload.keys_level_3),
      icon: getSearchKeyIcon(3),
      label: t("participants_section.host_reward_items.key_level_3"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
    {
      key: "key4",
      kind: "resource",
      amount: normalizeRewardAmount(payload.keys_level_4),
      amountLabel: formatRewardAmount(payload.keys_level_4),
      icon: getSearchKeyIcon(4),
      label: t("participants_section.host_reward_items.key_level_4"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
    {
      key: "key5",
      kind: "resource",
      amount: normalizeRewardAmount(payload.keys_level_5),
      amountLabel: formatRewardAmount(payload.keys_level_5),
      icon: getSearchKeyIcon(5),
      label: t("participants_section.host_reward_items.key_level_5"),
      iconClassName: "h-12 w-12 rotate-[45deg]",
    },
  ].filter((item) => item.amount > 0);
};

const resolveParticipantAvatar = (participant) => {
  return resolveUserAvatarSrc(
    participant,
    "/assets/profile/avatar_placeholder.png"
  );
};

const RequiredActionsModal = ({ onPreviewInvoiceCheckout = null }) => {
  const { t, i18n } = useTranslation("events");
  const navigate = useNavigate();
  const { openProfileCard } = useProfileCardModal();
  const location = useLocation();
  const {
    actions,
    open,
    dismissForSession,
    dismissPersistently,
    setOpen,
    consumeAction,
    refresh,
    focusedActionId,
  } = useContext(RequiredActionsContext);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [rewardBurst, setRewardBurst] = useState(null);
  const [declineConfirmArmed, setDeclineConfirmArmed] = useState(false);
  const [hostCancelConfirmArmed, setHostCancelConfirmArmed] = useState(false);
  const [conceptDatePollOpen, setConceptDatePollOpen] = useState(false);
  const [conceptDatePollCreated, setConceptDatePollCreated] = useState(false);
  const [badPhotoUploadOpen, setBadPhotoUploadOpen] = useState(false);

  const current = actions?.[currentIndex] || null;
  const total = actions?.length || 0;
  const progressCurrent = total ? currentIndex + 1 : 0;

  useEffect(() => {
    if (!total) {
      setCurrentIndex(0);
      return;
    }
    setCurrentIndex((prev) => Math.min(prev, total - 1));
  }, [total]);

  useEffect(() => {
    if (!open || !focusedActionId || !Array.isArray(actions)) return;
    const focusedIndex = actions.findIndex(
      (item) => String(item?.id || "") === String(focusedActionId)
    );
    if (focusedIndex >= 0) setCurrentIndex(focusedIndex);
  }, [actions, focusedActionId, open]);

  useEffect(() => {
    if (!rewardBurst) return undefined;
    const timer = window.setTimeout(() => {
      setRewardBurst(null);
    }, 900);
    return () => window.clearTimeout(timer);
  }, [rewardBurst]);

  const eventData = useMemo(() => {
    if (current?.event) return current.event;
    return current?.payload?.event_fallback || null;
  }, [current]);
  const paidRescheduleRefundAvailable = Boolean(
    current?.type === "activation_date_confirmation" &&
      current?.payload?.reason_kind === "reschedule" &&
      current?.payload?.paid_reschedule_refund_available
  );
  const isTimeOnlyScheduleChange = Boolean(
    current?.type === "activation_date_confirmation" &&
      (
        current?.payload?.schedule_change_kind === "time" ||
        eventData?.last_schedule_change_kind === "time"
      )
  );
  const hostPaidCancellationPolicy =
    current?.payload?.host_paid_cancellation_policy ||
    eventData?.host_paid_cancellation_policy ||
    null;
  const showHostPaidIntentPaymentNotice = Boolean(
    current?.type === "intent_payment" &&
      (
        hostPaidCancellationPolicy ||
        String(eventData?.pricing_model || "").trim().toLowerCase() === "host_paid"
      )
  );
  const ticketPaymentState = current?.payload?.ticket_payment_state || null;
  const paymentRequiresTicketChoice = Boolean(
    current?.type === "intent_payment" &&
      (
        ticketPaymentState?.multisport_pricing?.enabled ||
        eventData?.multisport_pricing_enabled ||
        eventData?.gender_pricing_enabled
      )
  );
  const isPremiumBoostExpiredNotice = current?.type === "premium_boost_expired_notice";
  const isPremiumMonthlyBenefits = current?.type === "premium_monthly_benefits";
  const isHostProcessingInvoiceDue = current?.type === "host_processing_invoice_due";
  const premiumChestAvailable = Boolean(
    isPremiumMonthlyBenefits && current?.payload?.chest_available
  );
  const premiumBoostAvailable = Boolean(
    isPremiumMonthlyBenefits
    && current?.payload?.boost_available
    && current?.payload?.has_boostable_event
  );
  const canOpenPremiumBoost = premiumBoostAvailable;
  const isWaitlistSpotAvailable = current?.type === "waitlist_spot_available";
  const isHostAttentionAction = current?.type === "host_participant_requests"
    || current?.type === "host_waitlist_activity";
  const canOpenCurrentEvent = Boolean(
    (current?.type === "participant_missed_notice" || isWaitlistSpotAvailable)
    && Number.isFinite(Number(eventData?.id))
    && Number(eventData?.id) > 0
  );
  const hasOpenConceptDatePoll = Boolean(
    current?.type === "concept_activation" &&
      (current?.payload?.has_open_date_poll || conceptDatePollCreated)
  );
  const hostRewardItems = useMemo(
    () => getRewardItems(current?.payload?.reward || {}, t),
    [current?.payload?.reward, t]
  );
  const isHostCompletionAction =
    current?.type === "host_success_reward" ||
    current?.type === "host_completion_failure";
  const attendanceReviewRequest = current?.payload?.attendance_review || null;
  const attendanceReviewStatus = String(attendanceReviewRequest?.status || "").trim().toLowerCase();
  const participantReviewData = current?.payload?.review_request || null;
  const reviewParticipant = participantReviewData?.participant || null;
  const reviewParticipantCode = String(reviewParticipant?.code || "").trim().toUpperCase();
  const canOpenReviewParticipantProfile = /^[A-Z0-9]{3}$/.test(reviewParticipantCode);
  const subscriptionPath = String(current?.payload?.subscription_path || "/menu/subscription");
  const subscriptionTierKey = String(current?.payload?.subscription_tier || "premium").trim().toLowerCase();
  const subscriptionTierLabel = t(
    `required_actions.subscription_tiers.${subscriptionTierKey}`,
    {
      defaultValue: current?.payload?.subscription_tier_label || "Premium",
    }
  );
  const canSendAttendanceReviewRequest = Boolean(
    current?.type === "participant_missed_notice" &&
      eventData?.id &&
      attendanceReviewStatus !== "pending" &&
      attendanceReviewStatus !== "approved" &&
      attendanceReviewStatus !== "rejected"
  );
  const requiresParticipantDeclineConfirmation =
    current?.type === "slot_confirmation" ||
    current?.type === "activation_date_confirmation" ||
    current?.type === "intent_confirmation" ||
    current?.type === "intent_payment";
  const moderationWarningTemplateKey =
    current?.type === "moderation_warning"
      ? String(current?.payload?.template_key || "")
      : "";

  useEffect(() => {
    setDeclineConfirmArmed(false);
    setHostCancelConfirmArmed(false);
  }, [current?.id, current?.type, eventData?.id, open]);

  useEffect(() => {
    setConceptDatePollOpen(false);
    setConceptDatePollCreated(false);
  }, [current?.id, current?.type, eventData?.id, open]);

  const sessionPath = current?.type === "join_party_session"
    ? String(
        current?.payload?.session_path
        || (eventData?.id ? `/events/${eventData.id}/party-session` : "")
      ).replace(/\/+$/, "")
    : "";
  const isAlreadyInsideRequestedSession = Boolean(
    sessionPath
    && String(location.pathname || "").replace(/\/+$/, "") === sessionPath
  );

  if (badPhotoUploadOpen) {
    return (
      <PhotoRequestModal
        visible
        allowClose
        titleOverride={t("required_actions.types.bad_photo_warning.upload_title")}
        subtitleOverride={t("required_actions.types.bad_photo_warning.description")}
        onUploaded={async () => {
          setBadPhotoUploadOpen(false);
          if (current?.id) consumeAction(current.id);
          await refresh({ silent: true });
        }}
        onClose={() => {
          setBadPhotoUploadOpen(false);
          setOpen(true);
        }}
      />
    );
  }

  if (!open || !current || isAlreadyInsideRequestedSession) return null;

  const notifyError = (message) => {
    if (!message) return;
    window.dispatchEvent(
      new CustomEvent("global-error", {
        detail: { message },
      })
    );
  };

  const notifyInfo = (message) => {
    if (!message) return;
    window.dispatchEvent(
      new CustomEvent("global-info", {
        detail: { message },
      })
    );
  };

  const completeAndRefresh = async () => {
    consumeAction(current.id);
    await refresh({ silent: true });
  };

  const claimHostCompletionReward = async () => {
    const eventId = eventData?.id;
    if (!eventId || submitting || redirecting) {
      const message = t("required_actions.errors.action_failed");
      notifyError(message);
      throw new Error(message);
    }

    setSubmitting(true);
    try {
      const { data } = await api.post(`/events/host-reward/claim/${eventId}/`);
      const rewardPayload = data?.reward || current?.payload?.reward || {};

      window.dispatchEvent(new CustomEvent("vg-vault-coins-collected"));
      window.dispatchEvent(
        new CustomEvent("vg-host-reward-claimed", {
          detail: { eventId, reward: rewardPayload },
        })
      );

      notifyInfo(t("required_actions.toasts.host_reward_claimed"));
      await completeAndRefresh();
      return false;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        t("required_actions.errors.action_failed");
      notifyError(message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const dismissHostCompletionFailure = async () => {
    if (submitting || redirecting || !current?.id) return;
    const eventId = eventData?.id;
    if (!eventId) {
      notifyError(t("required_actions.errors.action_failed"));
      return;
    }

    setSubmitting(true);
    try {
      await api.post(
        `/events/required-actions/host-completion-failure/${eventId}/ack/`
      );
      await completeAndRefresh();
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        t("required_actions.errors.action_failed");
      notifyError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const subscriptionActionIndices = (actions || [])
    .map((item, index) => (
      item?.type === "subscription_expired_notice" || item?.type === "club_suspended_notice"
        ? index
        : null
    ))
    .filter((value) => value != null);

  const subscriptionActionIds = subscriptionActionIndices
    .map((index) => actions?.[index]?.id)
    .filter(Boolean);

  const isSubscriptionNotice = (
    current?.type === "subscription_expired_notice"
    || current?.type === "club_suspended_notice"
  );

  if (isHostCompletionAction) {
    const isSuccessCompletion = current.type === "host_success_reward";

    return (
      <HostEventCompletionModal
        isOpen={Boolean(open && current)}
        event={eventData}
        outcome={isSuccessCompletion ? "success" : "failure"}
        attendance={current?.payload?.attendance || null}
        reward={current?.payload?.reward || null}
        onClaimReward={isSuccessCompletion ? claimHostCompletionReward : null}
        onClose={isSuccessCompletion ? dismissForSession : dismissHostCompletionFailure}
      />
    );
  }

  const advanceSubscriptionActionOrDismiss = () => {
    if (!isSubscriptionNotice) {
      dismissForSession();
      return;
    }

    const nextSubscriptionIndex = subscriptionActionIndices.find((index) => index > currentIndex);
    if (typeof nextSubscriptionIndex === "number") {
      setCurrentIndex(nextSubscriptionIndex);
      return;
    }

    dismissPersistently(subscriptionActionIds);
  };

  const acknowledgePremiumBenefits = () => {
    const actionId = String(current?.id || "");
    const monthKey = String(
      current?.payload?.month_key || actionId.split(":").pop() || ""
    );
    dismissPersistently(actionId);
    if (/^\d{4}-(0[1-9]|1[0-2])$/.test(monthKey)) {
      void api.post(`/events/required-actions/premium-benefits/${monthKey}/ack/`)
        .catch((err) => {
          console.error("[RequiredActionsModal] failed to acknowledge Premium benefits", err);
        });
    }
  };

  const handleDismiss = () => {
    if (submitting || redirecting) return;
    setDeclineConfirmArmed(false);
    if (current?.type === "bad_photo_warning") {
      void runAction(
        () => api.post("/events/required-actions/bad-photo-warning/ack/"),
        ""
      );
      return;
    }
    if (isSubscriptionNotice) {
      advanceSubscriptionActionOrDismiss();
      return;
    }
    if (isPremiumMonthlyBenefits) {
      acknowledgePremiumBenefits();
      return;
    }
    dismissForSession();
  };

  const handleOpenPremiumChest = () => {
    if (submitting || redirecting) return;
    acknowledgePremiumBenefits();
    navigate(String(current?.payload?.chest_path || "/vault/main?open_treasure_chest=1"));
  };

  const handleOpenPremiumBoost = () => {
    if (submitting || redirecting) return;
    acknowledgePremiumBenefits();
    navigate(String(current?.payload?.events_path || "/events/search"), {
      state: { audienceMode: "host" },
    });
  };

  const runAction = async (fn, successMessage) => {
    if (submitting || redirecting) return;
    setSubmitting(true);
    try {
      await fn();
      if (successMessage) {
        notifyInfo(successMessage);
      }
      await completeAndRefresh();
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        t("required_actions.errors.action_failed");
      notifyError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const runHostRoleRequestAction = async (decision, successMessage) => {
    const notificationId = Number(current?.payload?.notification_id || 0);
    if (!notificationId || submitting || redirecting) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/events/host-role-request/respond/`, {
        notification_id: notificationId,
        decision,
      });
      const expectedStatus = decision === "accept" ? "accepted" : "declined";
      if (String(response?.data?.status || "").trim().toLowerCase() !== expectedStatus) {
        consumeAction(current?.id);
        await refresh({ silent: true });
        return;
      }
      if (successMessage) {
        notifyInfo(successMessage);
      }
      await completeAndRefresh();
    } catch (err) {
      const rawMessage = String(
        err?.response?.data?.error ||
        err?.message ||
        ""
      ).trim();
      const normalizedMessage = rawMessage.toLowerCase();
      const isStaleProcessedRequest =
        normalizedMessage.includes("already processed")
        || normalizedMessage.includes("already a co-host")
        || normalizedMessage.includes("already event host");

      if (isStaleProcessedRequest) {
        consumeAction(current?.id);
        await refresh({ silent: true });
        return;
      }

      notifyError(rawMessage || t("required_actions.errors.action_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    setDeclineConfirmArmed(false);

    if (current.type === "bad_photo_warning") {
      if (submitting || redirecting) return;
      setOpen(false);
      setBadPhotoUploadOpen(true);
      return;
    }

    if (isSubscriptionNotice) {
      if (submitting || redirecting) return;
      dismissForSession();
      navigate(subscriptionPath);
      return;
    }

    if (current.type === "moderation_warning") {
      const warningId = Number(current?.payload?.warning_id || 0);
      if (!warningId) return;
      void runAction(
        () => api.post(`/events/required-actions/moderation-warning/${warningId}/ack/`),
        t("required_actions.types.moderation_warning.ack_toast")
      );
      return;
    }

    if (
      current.type === "auto_removed_notice"
      || current.type === "event_deleted_notice"
      || current.type === "premium_boost_expired_notice"
      || current.type === "participant_missed_notice"
      || current.type === "co_host_invite_cancelled_notice"
      || current.type === "host_transfer_cancelled_notice"
    ) {
      const notificationId = current?.payload?.notification_id;
      void runAction(async () => {
        if (notificationId) {
          await api.post(`/notifications/${notificationId}/read/`);
        }
      }, "");
      return;
    }

    if (current.type === "dinner_date_vote" || current.type === "dinner_final_confirmation") {
      dismissForSession();
      navigate(
        `/menu/dinners?focus=${current.type === "dinner_date_vote" ? "vote" : "confirm"}`
      );
      return;
    }

    if (current.type === "dinner_table_proposal") {
      void runAction(
        () => api.post("/accounts/dinners-match-respond/", {
          table_id: current?.payload?.table_id,
          decision: "confirm",
        }),
        t("required_actions.types.dinner_table_proposal.confirmed_toast")
      );
      return;
    }

    if (current.type === "co_host_invite_request" || current.type === "host_transfer_request") {
      void runHostRoleRequestAction(
        "accept",
        t("required_actions.toasts.host_role_request_accepted")
      );
      return;
    }

    const eventId = eventData?.id;
    if (!eventId) return;

    if (isHostAttentionAction) {
      dismissForSession();
      navigate(`/events/search?event=${eventId}&host_action=${
        current.type === "host_participant_requests" ? "requests" : "waitlist"
      }`);
      return;
    }

    if (current.type === "join_party_session") {
      dismissForSession();
      navigate(current?.payload?.session_path || `/events/${eventId}/party-session`);
      return;
    }

    if (current.type === "waitlist_spot_available") {
      dismissForSession();
      navigate(`/events/${eventId}?waitlist=1`);
      return;
    }

    if (current.type === "concept_interest_review") {
      void runAction(
        () => api.post(`/events/required-actions/concept-interest/${eventId}/extend/`),
        t("required_actions.types.concept_interest_review.extended_toast")
      );
      return;
    }

    if (current.type === "concept_activation") {
      if (submitting || redirecting) return;
      setSubmitting(true);
      (async () => {
        try {
          const [eventRes, hostRatingRes, pollRes] = await Promise.all([
            api.get(`/events/details/${eventId}/`),
            api.get(`/events/get-host-rating/`).catch(() => ({ data: null })),
            api.get(`/events/${eventId}/concept-date-poll/`).catch(() => ({ data: null })),
          ]);

          // Hide required-actions for current page lifecycle and take host to activation form.
          dismissForSession();
          navigate(`/events/edit?activate_concept=${eventId}`, {
            state: {
              mode: "activate_concept",
              eventData: eventRes?.data || eventData || { id: eventId, type: "concept" },
              conceptDatePoll: pollRes?.data?.poll || null,
              activationMode: true,
              hostRating: hostRatingRes?.data || null,
            },
          });
        } catch (err) {
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            t("required_actions.errors.action_failed");
          notifyError(msg);
        } finally {
          setSubmitting(false);
        }
      })();
      return;
    }

    if (current.type === "concept_date_poll") {
      openEventChatAtMessage(current?.payload?.chat_message_id);
      return;
    }

    if (current.type === "chat_message_attention") {
      openEventChatAtMessage(
        current?.payload?.chat_message_id || current?.payload?.message_id
      );
      return;
    }

    if (current.type === "chat_mention") {
      openEventChatAtMessage(
        current?.payload?.chat_message_id || current?.payload?.message_id
      );
      return;
    }

    if (current.type === "slot_confirmation") {
      void runAction(
        () => api.post(`/events/slot/confirm/${eventId}/`),
        t("required_actions.toasts.slot_confirmed")
      );
      return;
    }

    if (current.type === "activation_date_confirmation") {
      void runAction(
        () => api.post(`/events/activation-date/confirm/${eventId}/`),
        t(
          current?.payload?.counts_as_intent
            ? "confirm_intent_modal.toasts.activation_date_intent_confirmed"
            : isTimeOnlyScheduleChange
            ? "confirm_intent_modal.toasts.activation_time_confirmed"
            : "confirm_intent_modal.toasts.activation_date_confirmed"
        )
      );
      return;
    }

    if (current.type === "intent_confirmation") {
      void runAction(
        () => api.post(`/events/intent/confirm/${eventId}/`),
        t("confirm_intent_modal.toasts.intention_confirmed")
      );
      return;
    }

    if (current.type === "host_confirmation") {
      void runAction(
        () => api.post(`/events/host/confirm/${eventId}/`),
        t("events.details.host_confirmed_success")
      );
      return;
    }

    if (current.type === "host_success_reward") {
      if (submitting || redirecting) return;
      setSubmitting(true);
      (async () => {
        try {
          const { data } = await api.post(`/events/host-reward/claim/${eventId}/`);
          const rewardPayload = data?.reward || current?.payload?.reward || {};
          const rewardItems = getRewardItems(rewardPayload, t);
          const particles = rewardItems.slice(0, 8).map((item, idx) => ({
            id: `${Date.now()}-${item.key}-${idx}`,
            icon: item.icon,
            className: item.iconClassName,
            tx: `${150 + Math.round(Math.random() * 120)}px`,
            ty: `${-220 - Math.round(Math.random() * 120)}px`,
            delay: `${idx * 60}ms`,
          }));

          if (particles.length) {
            setRewardBurst({ particles, startedAt: Date.now() });
            await new Promise((resolve) => {
              window.setTimeout(resolve, 760);
            });
          }

          window.dispatchEvent(new CustomEvent("vg-vault-coins-collected"));
          window.dispatchEvent(
            new CustomEvent("vg-host-reward-claimed", {
              detail: { eventId, reward: rewardPayload },
            })
          );

          notifyInfo(t("required_actions.toasts.host_reward_claimed"));
          await completeAndRefresh();
        } catch (err) {
          const message =
            err?.response?.data?.error ||
            err?.message ||
            t("required_actions.errors.action_failed");
          notifyError(message);
        } finally {
          setSubmitting(false);
        }
      })();
      return;
    }

    if (current.type === "attendance_review_request") {
      const requestId = current?.payload?.review_request?.id;
      if (!requestId) return;
      void runAction(
        () =>
          api.post(`/events/${eventId}/attendance-review-request/decision/`, {
            request_id: requestId,
            decision: "approve",
          }),
        ""
      );
      return;
    }

  };

  const handleDecline = () => {
    if (submitting || redirecting) return;
    if (current.type === "host_confirmation" && !hostCancelConfirmArmed) {
      setHostCancelConfirmArmed(true);
      return;
    }
    if (requiresParticipantDeclineConfirmation && !declineConfirmArmed) {
      setDeclineConfirmArmed(true);
      return;
    }

    if (current.type === "co_host_invite_request" || current.type === "host_transfer_request") {
      void runHostRoleRequestAction(
        "decline",
        t("required_actions.toasts.host_role_request_declined")
      );
      return;
    }

    if (current.type === "dinner_table_proposal") {
      void runAction(
        () => api.post("/accounts/dinners-match-respond/", {
          table_id: current?.payload?.table_id,
          decision: "decline",
        }),
        t("required_actions.types.dinner_table_proposal.declined_toast")
      );
      return;
    }

    const eventId = eventData?.id;
    if (!eventId) return;

    if (current.type === "slot_confirmation") {
      void runAction(
        () => api.post(`/events/slot/decline/${eventId}/`),
        t("required_actions.toasts.slot_released")
      );
      return;
    }

    if (current.type === "activation_date_confirmation") {
      void runAction(
        () => api.post(`/events/activation-date/decline/${eventId}/`),
        t(
          paidRescheduleRefundAvailable
            ? "confirm_intent_modal.toasts.activation_date_refund_requested"
            : "confirm_intent_modal.toasts.activation_date_removed"
        )
      );
      return;
    }

    if (current.type === "intent_confirmation" || current.type === "intent_payment") {
      void runAction(
        () => api.post(`/events/intent/decline/${eventId}/`),
        t("confirm_intent_modal.toasts.removed_from_event")
      );
      return;
    }

    if (current.type === "host_confirmation") {
      void runAction(
        () => api.post(`/events/delete-event/${eventId}/`),
        t("events.details.deleted_success")
      );
      return;
    }

    if (current.type === "concept_interest_review") {
      void runAction(
        () => api.post(`/events/required-actions/concept-interest/${eventId}/unpublish/`),
        t("required_actions.types.concept_interest_review.unpublished_toast")
      );
      return;
    }

    if (current.type === "attendance_review_request") {
      const requestId = current?.payload?.review_request?.id;
      if (!requestId) return;
      void runAction(
        () =>
          api.post(`/events/${eventId}/attendance-review-request/decision/`, {
            request_id: requestId,
            decision: "reject",
          }),
        ""
      );
    }
  };

  const handlePay = async () => {
    if (redirecting || submitting) return;
    setDeclineConfirmArmed(false);
    const eventId = eventData?.id;
    const payUrl = current?.payload?.pay_url;
    const directUrl = resolveCheckoutUrl(payUrl, eventId);
    if (!directUrl) {
      notifyError(t("confirm_intent_modal.errors.no_checkout_url"));
      return;
    }

    setRedirecting(true);
    try {
      const url = await prepareEventCheckoutUrl({ directUrl, eventId });
      if (isNativeApp()) {
        await openExternalUrl(url);
        return;
      }
      if (isLikelyInAppBrowser()) {
        window.location.assign(url);
        return;
      }

      const popup = window.open(url, "_blank");
      if (!popup) {
        window.location.href = url;
      }
    } catch (err) {
      notifyError(t("confirm_intent_modal.errors.payment_start_failed"));
    } finally {
      setRedirecting(false);
    }
  };

  const handleChooseTicket = () => {
    const eventId = eventData?.id;
    if (!eventId || submitting || redirecting) return;

    // The event payment modal resolves the participant's applicable ticket
    // price and presents all available options before checkout starts.
    dismissForSession();
    navigate(`/events/${eventId}?force_payment=1`);
  };

  const handleApprovedRequestPayment = async () => {
    const eventId = eventData?.id;
    if (!eventId || submitting || redirecting) return;
    setSubmitting(true);
    try {
      // Check live capacity before opening the payment UI. The checkout
      // endpoint reserves the seat atomically when payment actually starts.
      const { data } = await api.post(`/events/join-event/${eventId}/`, {});
      if (data?.payment_required) {
        dismissForSession();
        navigate(`/events/${eventId}?force_payment=1`);
      } else if (data?.joined) {
        consumeAction(current.id);
        dismissForSession();
        navigate(`/events/${eventId}`);
      } else {
        notifyError(data?.error || t("required_actions.errors.action_failed"));
      }
    } catch (err) {
      notifyError(err?.response?.data?.error || t("required_actions.errors.action_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenCurrentEvent = () => {
    if (!canOpenCurrentEvent) return;
    dismissForSession();
    navigate(
      isWaitlistSpotAvailable
        ? `/events/${eventData.id}?waitlist=1`
        : `/events/${eventData.id}`
    );
  };

  const handleOpenBoostExpiredEvent = () => {
    const eventId = eventData?.id;
    const notificationId = current?.payload?.notification_id;
    if (!eventId || submitting || redirecting) return;
    setRedirecting(true);
    (async () => {
      try {
        if (notificationId) {
          await api.post(`/notifications/${notificationId}/read/`);
        }
        consumeAction(current.id);
        await refresh({ silent: true });
        dismissForSession();
        navigate(`/events/${eventId}`);
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          t("required_actions.errors.action_failed");
        notifyError(message);
      } finally {
        setRedirecting(false);
      }
    })();
  };

  const handleOpenConceptDatePoll = () => {
    const eventId = eventData?.id;
    if (!eventId || submitting || redirecting) return;
    setConceptDatePollOpen(true);
  };

  const handleConceptDatePollSaved = async () => {
    setConceptDatePollCreated(true);
    setConceptDatePollOpen(false);
    try {
      await refresh({ silent: true });
    } catch {
      // The poll itself is already saved. A background refresh failure must not
      // turn that successful action into an error in the date-poll modal.
    }
  };

  const handleOpenReviewParticipantProfile = () => {
    if (!canOpenReviewParticipantProfile) return;
    // Leave the action pending, but do not stack two focus-trapping modals.
    setOpen(false);
    openProfileCard({ ...reviewParticipant, code: reviewParticipantCode });
  };

  const handleOpenPlatformRules = () => {
    if (submitting || redirecting) return;
    setOpen(false);
    navigate("/about/tap-rules?from=required-actions");
  };

  const handleSendAttendanceReviewRequest = () => {
    const eventId = eventData?.id;
    if (!eventId || submitting || redirecting) return;
    void runAction(
      () =>
        api.post(`/events/${eventId}/attendance-review-request/`, {
          source: "required_action",
        }),
      t("required_actions.types.participant_missed_notice.review_request_pending")
    );
  };

  const handlePayHostProcessingInvoice = async () => {
    const invoiceId = Number(current?.payload?.invoice_id);
    if (!Number.isSafeInteger(invoiceId) || invoiceId <= 0 || submitting || redirecting) {
      notifyError(t("required_actions.errors.action_failed"));
      return;
    }
    setRedirecting(true);
    try {
      const response = await api.post(
        `/events/organizer-panel/invoices/${invoiceId}/checkout/`
      );
      const checkoutUrl = String(response?.data?.checkout_url || "").trim();
      const parsedUrl = new URL(checkoutUrl, window.location.origin);
      if (parsedUrl.protocol !== "https:" && parsedUrl.origin !== window.location.origin) {
        throw new Error("invalid_invoice_checkout_url");
      }
      if (isNativeApp()) {
        await openExternalUrl(parsedUrl.toString());
      } else {
        window.location.assign(parsedUrl.toString());
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.error ||
        t("required_actions.types.host_processing_invoice_due.checkout_error")
      );
    } finally {
      setRedirecting(false);
    }
  };

  const openEventChatAtMessage = (messageId) => {
    const eventId = eventData?.id;
    if (!eventId) return;
    const params = new URLSearchParams();
    const normalizedMessageId = Number(messageId || 0);
    if (normalizedMessageId > 0) {
      params.set("message_id", String(normalizedMessageId));
    }
    dismissForSession();
    navigate(
      `/menu/chats/event/${eventId}${params.toString() ? `?${params.toString()}` : ""}`
    );
  };

  const heading = (() => {
    switch (current.type) {
      case "bad_photo_warning":
        return t("required_actions.types.bad_photo_warning.heading");
      case "host_processing_invoice_due":
        return t("required_actions.types.host_processing_invoice_due.heading");
      case "moderation_warning":
        if (moderationWarningTemplateKey) {
          return t(
            `required_actions.types.moderation_warning.templates.${moderationWarningTemplateKey}.heading`,
            {
              defaultValue:
                current?.payload?.title || t("required_actions.types.moderation_warning.heading"),
            }
          );
        }
        return current?.payload?.title || t("required_actions.types.moderation_warning.heading");
      case "dinner_date_vote":
        return t("required_actions.types.dinner_date_vote.heading");
      case "dinner_table_proposal":
        return t("required_actions.types.dinner_table_proposal.heading");
      case "dinner_final_confirmation":
        return t("required_actions.types.dinner_final_confirmation.heading");
      case "slot_confirmation":
        return t("required_actions.types.slot_confirmation.heading");
      case "waitlist_spot_available":
        return t("required_actions.types.waitlist_spot_available.heading");
      case "host_participant_requests":
        return t("host_action_prompt.requests_title", { count: Number(current?.payload?.count || 0) });
      case "host_waitlist_activity":
        return t("host_action_prompt.waitlist_title");
      case "concept_activation":
        return t("required_actions.types.concept_activation.heading");
      case "concept_interest_review":
        return t("required_actions.types.concept_interest_review.heading");
      case "concept_date_poll":
        return t("required_actions.types.concept_date_poll.heading");
      case "chat_message_attention":
        return t("required_actions.types.chat_message_attention.heading");
      case "chat_mention":
        return t("required_actions.types.chat_mention.heading");
      case "join_party_session":
        return t(
          current?.payload?.session_kind === "sport"
            ? "required_actions.types.join_sport_session.heading"
            : "required_actions.types.join_party_session.heading"
        );
      case "activation_date_confirmation":
        return t(
          isTimeOnlyScheduleChange
            ? "required_actions.types.activation_date_confirmation.heading_time"
            : "required_actions.types.activation_date_confirmation.heading"
        );
      case "intent_payment":
        return t("required_actions.types.intent_payment.heading");
      case "approved_request_payment":
        return t("required_actions.types.approved_request_payment.heading");
      case "intent_confirmation":
        return t("required_actions.types.intent_confirmation.heading");
      case "host_confirmation":
        return t("required_actions.types.host_confirmation.heading");
      case "host_success_reward":
        return t("required_actions.types.host_success_reward.heading");
      case "auto_removed_notice":
        return t("required_actions.types.auto_removed_notice.heading");
      case "event_deleted_notice":
        return t("required_actions.types.event_deleted_notice.heading");
      case "participant_missed_notice":
        return t("required_actions.types.participant_missed_notice.heading");
      case "premium_boost_expired_notice":
        return t("required_actions.types.premium_boost_expired_notice.heading");
      case "premium_monthly_benefits":
        return t("required_actions.types.premium_monthly_benefits.heading");
      case "subscription_expired_notice":
        return t("required_actions.types.subscription_expired_notice.heading", {
          tierLabel: subscriptionTierLabel,
        });
      case "club_suspended_notice":
        return t("required_actions.types.club_suspended_notice.heading");
      case "attendance_review_request":
        return t("required_actions.types.attendance_review_request.heading");
      case "co_host_invite_request":
        return t("required_actions.types.co_host_invite_request.heading");
      case "co_host_invite_cancelled_notice":
        return t("required_actions.types.co_host_invite_cancelled_notice.heading");
      case "host_transfer_request":
        return t("required_actions.types.host_transfer_request.heading");
      case "host_transfer_cancelled_notice":
        return t("required_actions.types.host_transfer_cancelled_notice.heading");
      default:
        return t("required_actions.title");
    }
  })();

  const bodyParagraphs = (() => {
    switch (current.type) {
      case "bad_photo_warning":
        return splitParagraphs(t("required_actions.types.bad_photo_warning.description"));
      case "host_processing_invoice_due": {
        const amount = Number(current?.payload?.amount_due || 0);
        const amountLabel = new Intl.NumberFormat(i18n.language || "sk-SK", {
          style: "currency",
          currency: "EUR",
        }).format(Number.isFinite(amount) ? amount : 0);
        return splitParagraphs(
          t(current?.payload?.invoice_kind === "trusted_host_commission"
            ? "required_actions.types.host_processing_invoice_due.direct_description"
            : "required_actions.types.host_processing_invoice_due.description", {
            event: current?.payload?.event_name || eventData?.name || t("required_actions.fallback.event"),
            amount: amountLabel,
          })
        );
      }
      case "moderation_warning":
        if (moderationWarningTemplateKey) {
          return splitParagraphs(
            t(
              `required_actions.types.moderation_warning.templates.${moderationWarningTemplateKey}.description`,
              {
                defaultValue:
                  current?.payload?.message ||
                  t("required_actions.types.moderation_warning.description"),
              }
            )
          );
        }
        return splitParagraphs(
          current?.payload?.message || t("required_actions.types.moderation_warning.description")
        );
      case "dinner_date_vote":
        return splitParagraphs(t("required_actions.types.dinner_date_vote.description"));
      case "dinner_table_proposal": {
        const deadline = formatDateTime(current?.payload?.deadline_at, i18n.language);
        return splitParagraphs(
          t("required_actions.types.dinner_table_proposal.description", {
            city: current?.payload?.city || "",
            days: Array.isArray(current?.payload?.common_days)
              ? current.payload.common_days.join(", ")
              : "",
            deadline,
          })
        );
      }
      case "dinner_final_confirmation":
        return splitParagraphs(
          t(
            Number(current?.payload?.flow_version || 1) >= 2
              ? "required_actions.types.dinner_final_confirmation.description_managed"
              : "required_actions.types.dinner_final_confirmation.description",
            {
            deposit: Number(current?.payload?.deposit_amount_diamonds || 0),
            }
          )
        );
      case "slot_confirmation": {
        const deadline = formatDateTime(current?.payload?.deadline_at, i18n.language);
        if (!deadline) {
          return splitParagraphs(t("required_actions.types.slot_confirmation.description"));
        }
        return splitParagraphs(
          t("required_actions.types.slot_confirmation.description_with_deadline", {
            deadline,
          })
        );
      }
      case "waitlist_spot_available":
        return splitParagraphs(
          t("required_actions.types.waitlist_spot_available.description", {
            event: eventData?.name || t("required_actions.fallback.event"),
          })
        );
      case "host_participant_requests":
      case "host_waitlist_activity":
        return splitParagraphs(t(
          current.type === "host_participant_requests"
            ? "host_action_prompt.requests_body"
            : "host_action_prompt.waitlist_body",
          {
            count: Number(current?.payload?.count || 0),
            eventName: eventData?.name || t("host_action_prompt.this_event"),
          }
        ));
      case "concept_activation": {
        const deadline = formatDateTime(current?.payload?.deadline_at, i18n.language);
        if (!deadline) {
          const base = t("required_actions.types.concept_activation.description");
          if (hasOpenConceptDatePoll) return splitParagraphs(base);
          return splitParagraphs(
            base,
            t("required_actions.types.concept_activation.description_date_poll_hint")
          );
        }
        const participantCount = Number(current?.payload?.participant_count || 0);
        const maxParticipants = Number(current?.payload?.max_participants || 0);
        const descriptionKey = maxParticipants > 0
          ? "required_actions.types.concept_activation.description_with_count_and_deadline"
          : "required_actions.types.concept_activation.description_with_deadline";
        const base = t(descriptionKey, {
          deadline,
          participants: participantCount,
          capacity: maxParticipants,
        });
        if (hasOpenConceptDatePoll) return splitParagraphs(base);
        return splitParagraphs(
          base,
          t("required_actions.types.concept_activation.description_date_poll_hint")
        );
      }
      case "concept_interest_review": {
        const deadline = formatDateTime(
          current?.payload?.response_deadline_at,
          i18n.language
        );
        const reason = String(current?.payload?.reason || "").trim();
        const introKey = reason === "no_participants"
          ? "required_actions.types.concept_interest_review.description_no_participants"
          : "required_actions.types.concept_interest_review.description_insufficient_participants";
        const paragraphs = [
          t(introKey),
          t("required_actions.types.concept_interest_review.description_extension", {
            days: Number(current?.payload?.extension_days || 7),
          }),
        ];
        if (deadline) {
          paragraphs.push(
            t("required_actions.types.concept_interest_review.description_deadline", {
              deadline,
            })
          );
        }
        return splitParagraphs(...paragraphs);
      }
      case "concept_date_poll": {
        return splitParagraphs(t("required_actions.types.concept_date_poll.description"));
      }
      case "chat_message_attention": {
        return splitParagraphs(t("required_actions.types.chat_message_attention.description"));
      }
      case "chat_mention": {
        return splitParagraphs(
          t("required_actions.types.chat_mention.description", {
            event: eventData?.name || t("required_actions.fallback.event"),
          })
        );
      }
      case "join_party_session": {
        return splitParagraphs(
          t(
            current?.payload?.session_kind === "sport"
              ? "required_actions.types.join_sport_session.description"
              : "required_actions.types.join_party_session.description"
          )
        );
      }
      case "activation_date_confirmation": {
        const reason = current?.payload?.reason_kind;
        const countsAsIntent = Boolean(current?.payload?.counts_as_intent);
        if (reason === "concept_activation") {
          return splitParagraphs(
            t(
              countsAsIntent
                ? "required_actions.types.activation_date_confirmation.description_concept_activation_counts_as_intent"
                : "required_actions.types.activation_date_confirmation.description_concept_activation"
            )
          );
        }
        if (reason === "reschedule") {
          return splitParagraphs(
            t(
              isTimeOnlyScheduleChange && paidRescheduleRefundAvailable
                ? "required_actions.types.activation_date_confirmation.description_reschedule_time_paid_refund"
                : isTimeOnlyScheduleChange && countsAsIntent
                ? "required_actions.types.activation_date_confirmation.description_reschedule_time_counts_as_intent"
                : isTimeOnlyScheduleChange
                ? "required_actions.types.activation_date_confirmation.description_reschedule_time"
                : paidRescheduleRefundAvailable
                ? "required_actions.types.activation_date_confirmation.description_reschedule_paid_refund"
                : countsAsIntent
                ? "required_actions.types.activation_date_confirmation.description_reschedule_counts_as_intent"
                : "required_actions.types.activation_date_confirmation.description_reschedule"
            )
          );
        }
        return splitParagraphs(
          t(
            countsAsIntent
              ? "required_actions.types.activation_date_confirmation.description_counts_as_intent"
              : "required_actions.types.activation_date_confirmation.description"
          )
        );
      }
      case "intent_payment":
        return splitParagraphs(
          t(
            current?.payload?.payment_step === "remaining_balance"
              ? "required_actions.types.intent_payment.description_remaining_balance"
              : "required_actions.types.intent_payment.description"
          )
        );
      case "approved_request_payment":
        return splitParagraphs(t("required_actions.types.approved_request_payment.description"));
      case "intent_confirmation":
        return splitParagraphs(t("required_actions.types.intent_confirmation.description"));
      case "host_confirmation":
        return splitParagraphs(t("required_actions.types.host_confirmation.description"));
      case "host_success_reward":
        return splitParagraphs(t("required_actions.types.host_success_reward.description"));
      case "auto_removed_notice": {
        const reason = current?.payload?.reason_kind;
        if (reason === "activation_date_timeout") {
          return splitParagraphs(t("required_actions.types.auto_removed_notice.description_activation"));
        }
        if (reason === "waitlist_slot_timeout") {
          return splitParagraphs(t("required_actions.types.auto_removed_notice.description_waitlist"));
        }
        return splitParagraphs(t("required_actions.types.auto_removed_notice.description_intent"));
      }
      case "event_deleted_notice": {
        const reason = current?.payload?.reason_kind;
        if (reason === "event_deleted_by_host") {
          return splitParagraphs(
            t("required_actions.types.event_deleted_notice.description_deleted_by_host")
          );
        }
        if (reason === "event_deleted_host_confirmation_timeout") {
          return splitParagraphs(
            t("required_actions.types.event_deleted_notice.description_host_timeout")
          );
        }
        if (reason === "event_deleted_concept_not_activated") {
          return splitParagraphs(
            t("required_actions.types.event_deleted_notice.description_concept_timeout")
          );
        }
        return splitParagraphs(t("required_actions.types.event_deleted_notice.description_generic"));
      }
      case "participant_missed_notice": {
        const parts = [t("required_actions.types.participant_missed_notice.description")];
        if (attendanceReviewStatus === "pending") {
          parts.push(t("required_actions.types.participant_missed_notice.review_request_pending"));
        } else if (canSendAttendanceReviewRequest) {
          parts.push(t("required_actions.types.participant_missed_notice.review_request_hint"));
          parts.push(t("required_actions.types.participant_missed_notice.review_request_penalty"));
          parts.push(t("required_actions.types.participant_missed_notice.review_request_photo_hint"));
        }
        return splitParagraphs(...parts);
      }
      case "premium_boost_expired_notice":
        return splitParagraphs(t("required_actions.types.premium_boost_expired_notice.description"));
      case "premium_monthly_benefits":
        return splitParagraphs(
          premiumChestAvailable
            ? t("required_actions.types.premium_monthly_benefits.chest_description")
            : "",
          premiumBoostAvailable
            ? t("required_actions.types.premium_monthly_benefits.boost_description")
            : ""
        );
      case "subscription_expired_notice":
        return splitParagraphs(
          t("required_actions.types.subscription_expired_notice.description", {
            tierLabel: subscriptionTierLabel,
          })
        );
      case "club_suspended_notice": {
        const clubCount = Number(current?.payload?.club_count || 0);
        return splitParagraphs(
          t(
            clubCount > 1
              ? "required_actions.types.club_suspended_notice.description_many"
              : "required_actions.types.club_suspended_notice.description_one",
            { count: clubCount || 1 }
          )
        );
      }
      case "attendance_review_request":
        return splitParagraphs(t("required_actions.types.attendance_review_request.description"));
      case "co_host_invite_request": {
        const requesterCode = current?.payload?.requester?.code || "";
        return splitParagraphs(
          t("required_actions.types.co_host_invite_request.description", {
            requester: requesterCode || t("required_actions.fallback.user"),
          })
        );
      }
      case "co_host_invite_cancelled_notice": {
        const requesterCode = current?.payload?.requester?.code || "";
        return splitParagraphs(
          t("required_actions.types.co_host_invite_cancelled_notice.description", {
            requester: requesterCode || t("required_actions.fallback.user"),
          })
        );
      }
      case "host_transfer_request": {
        const requesterUsername = String(
          current?.payload?.requester?.username
          || current?.payload?.requester?.code
          || ""
        ).trim();
        const sourceTier = String(
          current?.payload?.source_subscription_tier
          || current?.payload?.effective_subscription_tier
          || "free"
        ).trim().toLowerCase();
        const targetTier = String(
          current?.payload?.target_subscription_tier
          || current?.payload?.effective_subscription_tier
          || "free"
        ).trim().toLowerCase();
        const isPremiumTier = (tier) => ["premium", "staff"].includes(tier);
        const sourceIsPremium = isPremiumTier(sourceTier);
        const targetIsPremium = isPremiumTier(targetTier);
        const paragraphs = splitParagraphs(
          t("required_actions.types.host_transfer_request.description", {
            requester: requesterUsername || t("required_actions.fallback.user"),
          })
        );

        if (sourceIsPremium && !targetIsPremium) {
          paragraphs.push(
            t("required_actions.types.host_transfer_request.premium_to_free")
          );
        } else if (!sourceIsPremium && targetIsPremium) {
          paragraphs.push(
            t("required_actions.types.host_transfer_request.free_to_premium")
          );
        }

        return paragraphs;
      }
      case "host_transfer_cancelled_notice": {
        const requesterCode = current?.payload?.requester?.code || "";
        return splitParagraphs(
          t("required_actions.types.host_transfer_cancelled_notice.description", {
            requester: requesterCode || t("required_actions.fallback.user"),
          })
        );
      }
      default:
        return [];
    }
  })();

  const priceRaw = current?.payload?.price;
  const price = Number.isFinite(Number(priceRaw)) ? Number(priceRaw) : null;
  const isInformationalNotice =
    current?.type === "auto_removed_notice"
    || current?.type === "event_deleted_notice"
    || current?.type === "premium_boost_expired_notice"
    || current?.type === "participant_missed_notice"
    || current?.type === "co_host_invite_cancelled_notice"
    || current?.type === "host_transfer_cancelled_notice";
  const isModerationWarning = current?.type === "moderation_warning";
  const modalTitle = isPremiumMonthlyBenefits
    ? heading
    : isHostAttentionAction
    ? heading
    : isWaitlistSpotAvailable
    ? t("required_actions.types.waitlist_spot_available.modal_title")
    : isModerationWarning
    ? heading
    : (
      isInformationalNotice
        ? t("required_actions.recent_title")
        : t("required_actions.title")
    );
  const displayedHeading = hostCancelConfirmArmed
    ? t("events.details.host_cancel_modal.title")
    : heading;
  const displayedBodyParagraphs = hostCancelConfirmArmed
    ? splitParagraphs(t("events.details.host_cancel_modal.message"))
    : bodyParagraphs;
  const getParticipantDeclineButtonClassName = (busy) => `w-full h-12 rounded-2xl font-semibold text-base transition ${
    busy
      ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
      : requiresParticipantDeclineConfirmation && declineConfirmArmed
      ? "bg-[#EF4458] text-white shadow-[0_10px_28px_rgba(239,68,88,0.24)]"
      : "bg-white text-red-600"
  }`;
  const getParticipantDeclineButtonLabel = (fallback) =>
    requiresParticipantDeclineConfirmation && declineConfirmArmed
      ? t("confirm_intent_modal.buttons.confirm_leave_event")
      : fallback;
  const eventAvatar =
    eventData?.avatar_url ||
    eventData?.host_avatar_url ||
    eventData?.host?.avatar_custom_url ||
    null;

  const eventCard = eventData ? (
    <div
      className={`w-full overflow-hidden rounded-2xl bg-black/30 ${
        canOpenCurrentEvent ? "cursor-pointer" : ""
      }`}
      onClick={canOpenCurrentEvent ? handleOpenCurrentEvent : undefined}
      onKeyDown={
        canOpenCurrentEvent
          ? (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleOpenCurrentEvent();
            }
          }
          : undefined
      }
      role={canOpenCurrentEvent ? "button" : undefined}
      tabIndex={canOpenCurrentEvent ? 0 : undefined}
      aria-label={
        canOpenCurrentEvent
          ? t(
              isWaitlistSpotAvailable
                ? "required_actions.types.waitlist_spot_available.open_event_aria"
                : "required_actions.types.participant_missed_notice.open_event_aria"
            )
          : undefined
      }
    >
      <EventCard
        title={eventData?.name || t("required_actions.fallback.event")}
        avatar={eventAvatar}
        isOnline={Number(eventData?.event_format) === 0}
        isPersonal={Number(eventData?.host_format) === 0}
        isPaid={Boolean(
          eventData?.is_payable_in_advance ||
            eventData?.show_pay_button ||
            (eventData?.payment_category_1 && Number(eventData.payment_category_1) > 0)
        )}
        datetime={eventData?.date_time || ""}
        isVerified={false}
        isValidated={true}
        minVaultLevel={eventData?.min_vault_level}
        color="#ffffff"
        isFavourite={false}
        onToggleFavourite={() => {}}
        isSelected={false}
        fromMap={false}
        status={eventData?.status || "active"}
        badgeLabel={eventData?.badge_label ?? eventData?.badge_label_id ?? null}
        iconImageUrl={eventData?.icon_image_url}
        isPromoted={false}
        isMainEvent={false}
        unreadCount={0}
        type={eventData?.type || "event"}
        titleMaxWidthClass="max-w-[72vw] md:max-w-[420px]"
      />
    </div>
  ) : null;
  const shouldShowEventCard = Boolean(eventCard);
  const participantReviewCard =
    current?.type === "attendance_review_request" && reviewParticipant ? (
      <div className="mt-4 flex justify-center">
        <div className="inline-flex max-w-full items-center gap-4">
          {canOpenReviewParticipantProfile ? (
            <button
              type="button"
              onClick={handleOpenReviewParticipantProfile}
              aria-label={t("required_actions.types.attendance_review_request.open_profile_aria")}
              className="shrink-0 rounded-full transition hover:scale-[1.02] focus:outline-none"
            >
              <img
                src={resolveParticipantAvatar(reviewParticipant)}
                alt=""
                className="h-24 w-24 rounded-full object-cover ring-2 ring-white/25"
              />
            </button>
          ) : (
            <img
              src={resolveParticipantAvatar(reviewParticipant)}
              alt=""
              className="h-24 w-24 rounded-full object-cover ring-2 ring-white/25"
            />
          )}
          <div className="min-w-0 max-w-[180px] text-left sm:max-w-[220px]">
            <div className="truncate text-xl font-semibold leading-6 text-white">
              {reviewParticipant?.code ||
                reviewParticipant?.username ||
                t("required_actions.types.attendance_review_request.participant_label")}
            </div>
            {reviewParticipant?.username && reviewParticipant?.username !== reviewParticipant?.code ? (
              <div className="mt-1 truncate text-sm text-white/60">
                @{reviewParticipant.username}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    ) : null;

  const handleNextAction = () => {
    if (total <= 1 || submitting || redirecting) return;
    setCurrentIndex((prev) => Math.min(prev + 1, total - 1));
  };

  const handlePreviousAction = () => {
    if (total <= 1 || submitting || redirecting) return;
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const modalFooter = isHostProcessingInvoiceDue ? (
    <div className="grid grid-cols-1 gap-3">
      <button
        type="button"
        onClick={handlePayHostProcessingInvoice}
        disabled={submitting || redirecting}
        className="h-12 rounded-2xl bg-white font-semibold text-blue-600 transition disabled:cursor-wait disabled:opacity-50"
      >
        {redirecting
          ? t("required_actions.types.host_processing_invoice_due.opening_checkout")
          : t("required_actions.types.host_processing_invoice_due.pay_button")}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        disabled={submitting || redirecting}
        className="h-12 rounded-2xl bg-white/20 font-semibold text-white transition disabled:cursor-wait disabled:opacity-50"
      >
        {t("required_actions.types.host_processing_invoice_due.later_button")}
      </button>
    </div>
  ) : isPremiumMonthlyBenefits ? (
      <div
        className={`grid grid-cols-1 gap-3 ${
          premiumChestAvailable && canOpenPremiumBoost
            ? "sm:grid-cols-2"
            : "sm:mx-auto sm:w-1/2"
        }`}
      >
        {premiumChestAvailable ? (
          <button
            type="button"
            onClick={handleOpenPremiumChest}
            disabled={submitting || redirecting}
            className="h-12 rounded-2xl bg-white font-semibold text-base text-blue-600 transition disabled:cursor-wait disabled:opacity-50"
          >
            {t("required_actions.types.premium_monthly_benefits.open_chest_button")}
          </button>
        ) : null}
        {canOpenPremiumBoost ? (
          <button
            type="button"
            onClick={handleOpenPremiumBoost}
            disabled={submitting || redirecting}
            className="h-12 rounded-2xl bg-white/20 font-semibold text-base text-white transition disabled:cursor-wait disabled:opacity-50"
          >
            {t("required_actions.types.premium_monthly_benefits.boost_event_button")}
          </button>
        ) : null}
      </div>
      ) : current.type === "bad_photo_warning" ? (
      <div className="grid grid-cols-1 gap-3">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || redirecting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting || redirecting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white text-blue-600"
          }`}
        >
          {t("required_actions.types.bad_photo_warning.change_button")}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={submitting || redirecting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting || redirecting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white/20 text-white"
          }`}
        >
          {t("required_actions.types.bad_photo_warning.later_button")}
        </button>
      </div>
      ) : current.type === "moderation_warning" ? (
      <div className="grid grid-cols-1 gap-3">
        <button
          type="button"
          onClick={handleOpenPlatformRules}
          disabled={submitting || redirecting}
          className={`w-full h-12 rounded-2xl border border-white/14 bg-white/10 font-semibold text-base text-white transition ${
            submitting || redirecting
              ? "cursor-wait opacity-55"
              : "active:scale-[0.985] hover:bg-white/14"
          }`}
        >
          {t("required_actions.types.moderation_warning.rules_button")}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white text-blue-600"
          }`}
        >
          {t("required_actions.types.moderation_warning.ack_button")}
        </button>
      </div>
      ) : isPremiumBoostExpiredNotice ? (
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={handleOpenBoostExpiredEvent}
            disabled={submitting || redirecting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting || redirecting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-blue-600"
            }`}
          >
            {t("required_actions.types.premium_boost_expired_notice.open_event_button")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || redirecting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting || redirecting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white/15 text-white"
            }`}
          >
            {t("required_actions.types.premium_boost_expired_notice.ok_button")}
          </button>
        </div>
      ) : isHostAttentionAction ? (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || redirecting}
          className="w-full h-12 rounded-2xl bg-white font-semibold text-blue-600 transition disabled:cursor-wait disabled:opacity-50"
        >
          {t(current.type === "host_participant_requests"
            ? "host_action_prompt.requests_cta"
            : "host_action_prompt.waitlist_cta")}
        </button>
      ) : isWaitlistSpotAvailable ? (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || redirecting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting || redirecting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white text-blue-600"
          }`}
        >
          {t("required_actions.types.waitlist_spot_available.open_event_button")}
        </button>
      ) : current.type === "auto_removed_notice" || current.type === "event_deleted_notice" || current.type === "participant_missed_notice" || current.type === "co_host_invite_cancelled_notice" || current.type === "host_transfer_cancelled_notice" ? (
        <div className="grid grid-cols-1 gap-3">
          {current.type === "participant_missed_notice" && canSendAttendanceReviewRequest ? (
            <button
              type="button"
              onClick={handleSendAttendanceReviewRequest}
              disabled={submitting}
              className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
                submitting
                  ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                  : "bg-white/15 text-white"
              }`}
            >
              {t("required_actions.types.participant_missed_notice.review_request_button")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-blue-600"
            }`}
          >
            {current.type === "event_deleted_notice"
              ? t("required_actions.types.event_deleted_notice.ok_button")
              : current.type === "participant_missed_notice"
              ? t("required_actions.types.participant_missed_notice.ok_button")
              : current.type === "co_host_invite_cancelled_notice"
              ? t("required_actions.types.co_host_invite_cancelled_notice.ok_button")
              : current.type === "host_transfer_cancelled_notice"
              ? t("required_actions.types.host_transfer_cancelled_notice.ok_button")
              : t("required_actions.types.auto_removed_notice.ok_button")}
          </button>
        </div>
      ) : current.type === "attendance_review_request" ? (
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-blue-600"
            }`}
          >
            {t("required_actions.types.attendance_review_request.approve_button")}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-red-600"
            }`}
          >
            {t("required_actions.types.attendance_review_request.reject_button")}
          </button>
        </div>
      ) : current.type === "co_host_invite_request" || current.type === "host_transfer_request" ? (
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-blue-600"
            }`}
          >
            {t("required_actions.types.host_role_request.accept_button")}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-red-600"
            }`}
          >
            {t("required_actions.types.host_role_request.decline_button")}
          </button>
        </div>
      ) : isSubscriptionNotice ? (
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || redirecting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting || redirecting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-blue-600"
            }`}
          >
            {current.type === "subscription_expired_notice"
              ? t("required_actions.types.subscription_expired_notice.renew_button", {
                  tierLabel: subscriptionTierLabel,
                })
              : t("required_actions.types.club_suspended_notice.renew_button")}
          </button>
          <button
            type="button"
            onClick={advanceSubscriptionActionOrDismiss}
            disabled={submitting || redirecting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting || redirecting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white/20 text-white"
            }`}
          >
            {current.type === "subscription_expired_notice"
              ? t("required_actions.types.subscription_expired_notice.later_button")
              : t("required_actions.types.club_suspended_notice.later_button")}
          </button>
        </div>
      ) : current.type === "dinner_table_proposal" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || redirecting}
            className="h-12 rounded-2xl bg-white font-semibold text-blue-600 transition disabled:cursor-wait disabled:opacity-50"
          >
            {t("required_actions.types.dinner_table_proposal.confirm_button")}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting || redirecting}
            className="h-12 rounded-2xl bg-white/20 font-semibold text-white transition disabled:cursor-wait disabled:opacity-50"
          >
            {t("required_actions.types.dinner_table_proposal.decline_button")}
          </button>
        </div>
      ) : current.type === "dinner_date_vote" || current.type === "dinner_final_confirmation" ? (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || redirecting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting || redirecting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white text-blue-600"
          }`}
        >
          {current.type === "dinner_date_vote"
            ? t("required_actions.types.dinner_date_vote.open_button")
            : t("required_actions.types.dinner_final_confirmation.open_button")}
        </button>
      ) : current.type === "concept_interest_review" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || redirecting}
            className="h-12 rounded-2xl bg-white font-semibold text-blue-600 transition disabled:cursor-wait disabled:opacity-50"
          >
            {t("required_actions.types.concept_interest_review.extend_button")}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting || redirecting}
            className="h-12 rounded-2xl bg-red-500/20 font-semibold text-red-100 transition disabled:cursor-wait disabled:opacity-50"
          >
            {t("required_actions.types.concept_interest_review.unpublish_button")}
          </button>
        </div>
      ) : current.type === "concept_activation" ? (
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-blue-600"
            }`}
          >
            {t("required_actions.types.concept_activation.activate_button")}
          </button>
          {!hasOpenConceptDatePoll ? (
            <button
              type="button"
              onClick={handleOpenConceptDatePoll}
              disabled={submitting || redirecting}
              className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
                submitting || redirecting
                  ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                  : "bg-white/20 text-white"
              }`}
            >
              {t("required_actions.types.concept_activation.send_date_poll_button")}
            </button>
          ) : null}
        </div>
      ) : current.type === "concept_date_poll" ? (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || redirecting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting || redirecting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white text-blue-600"
          }`}
        >
          {t("required_actions.types.concept_date_poll.open_chat_button")}
        </button>
      ) : current.type === "chat_message_attention" || current.type === "chat_mention" ? (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || redirecting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting || redirecting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white text-blue-600"
          }`}
        >
          {t(
            current.type === "chat_mention"
              ? "required_actions.types.chat_mention.open_chat_button"
              : "required_actions.types.chat_message_attention.open_chat_button"
          )}
        </button>
      ) : current.type === "join_party_session" ? (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting || redirecting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting || redirecting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white text-blue-600"
          }`}
        >
          {t(
            current?.payload?.session_kind === "sport"
              ? "required_actions.types.join_sport_session.open_button"
              : "required_actions.types.join_party_session.open_button"
          )}
        </button>
      ) : current.type === "approved_request_payment" ? (
        <button
          type="button"
          onClick={handleApprovedRequestPayment}
          disabled={submitting || redirecting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting || redirecting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white text-blue-600"
          }`}
        >
          {t("required_actions.types.approved_request_payment.pay_button")}
        </button>
      ) : current.type === "intent_payment" ? (
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={paymentRequiresTicketChoice ? handleChooseTicket : handlePay}
            disabled={submitting || redirecting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting || redirecting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-blue-600"
            }`}
          >
            {redirecting
              ? t("confirm_intent_modal.buttons.pay_opening_checkout")
              : paymentRequiresTicketChoice
              ? t("required_actions.types.intent_payment.choose_ticket_button")
              : price
              ? t("confirm_intent_modal.buttons.pay_with_price", {
                  price: price.toFixed(2),
                })
              : t("confirm_intent_modal.buttons.pay_now")}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting || redirecting}
            className={getParticipantDeclineButtonClassName(submitting || redirecting)}
          >
            {getParticipantDeclineButtonLabel(t("confirm_intent_modal.buttons.cant_attend"))}
          </button>
        </div>
      ) : current.type === "host_confirmation" ? (
        <div className="grid grid-cols-1 gap-3">
          {!hostCancelConfirmArmed ? (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
                submitting
                  ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                  : "bg-white text-blue-600"
              }`}
            >
              {t("events.details.host_confirm_modal.confirm_button")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-red-600"
            }`}
          >
            {t(
              hostCancelConfirmArmed
                ? "events.details.host_cancel_modal.confirm_button"
                : "events.details.host_confirm_modal.cancel_event_button"
            )}
          </button>
          {hostCancelConfirmArmed ? (
            <button
              type="button"
              onClick={() => setHostCancelConfirmArmed(false)}
              disabled={submitting}
              className="w-full h-12 rounded-2xl bg-white font-semibold text-base text-blue-600 transition disabled:cursor-wait disabled:opacity-50"
            >
              {t("events.details.host_cancel_modal.back_button")}
            </button>
          ) : null}
        </div>
      ) : current.type === "host_success_reward" ? (
        <button
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
          className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
            submitting
              ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
              : "bg-white text-blue-600"
          }`}
        >
          {t("required_actions.types.host_success_reward.claim_button")}
        </button>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className={`w-full h-12 rounded-2xl font-semibold text-base transition ${
              submitting
                ? "bg-white bg-opacity-20 text-gray-500 cursor-wait"
                : "bg-white text-blue-600"
            }`}
          >
            {current.type === "slot_confirmation"
              ? t("required_actions.types.slot_confirmation.confirm_button")
              : current.type === "activation_date_confirmation"
              ? t(
                  current?.payload?.counts_as_intent
                    ? "confirm_intent_modal.buttons.confirm_date_intent_idle"
                    : isTimeOnlyScheduleChange
                    ? "confirm_intent_modal.buttons.confirm_time_idle"
                    : "confirm_intent_modal.buttons.confirm_date_idle"
                )
              : t("confirm_intent_modal.buttons.confirm_idle")}
          </button>
          <button
            type="button"
            onClick={handleDecline}
            disabled={submitting}
            className={getParticipantDeclineButtonClassName(submitting)}
          >
            {getParticipantDeclineButtonLabel(
              current.type === "slot_confirmation"
                ? t("required_actions.types.slot_confirmation.decline_button")
                : current.type === "activation_date_confirmation"
                ? t(
                    paidRescheduleRefundAvailable
                      ? "confirm_intent_modal.buttons.decline_date_refund"
                      : current?.payload?.counts_as_intent
                      ? "confirm_intent_modal.buttons.decline_changed_plans"
                      : isTimeOnlyScheduleChange
                      ? "confirm_intent_modal.buttons.decline_time_not_suitable"
                      : "confirm_intent_modal.buttons.decline_date_not_suitable"
                  )
                : t("confirm_intent_modal.buttons.decline_changed_plans")
            )}
          </button>
        </div>
      );

  const rewardBurstOverlay = rewardBurst?.particles?.length ? (
        <>
          {rewardBurst.particles.map((particle) => (
            <img
              key={particle.id}
              src={particle.icon}
              alt=""
              className={`absolute left-1/2 top-[72%] ${particle.className}`}
              style={{
                animationName: "vgRewardFly",
                animationDuration: "720ms",
                animationTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
                animationFillMode: "forwards",
                animationDelay: particle.delay,
                "--vg-reward-tx": particle.tx,
                "--vg-reward-ty": particle.ty,
              }}
            />
          ))}
        </>
      ) : null;

  return (
    <>
      <AppModalShell
        open={Boolean(open && current)}
        title={modalTitle}
        onClose={handleDismiss}
        closeAriaLabel={t("required_actions.close_aria")}
        zIndexClassName="z-[13020]"
        maxWidthClassName="max-w-[34rem]"
        footer={modalFooter}
        overlayContent={rewardBurstOverlay}
      >
      {total > 1 ? (
        <div className="mb-4 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={handlePreviousAction}
            disabled={submitting || redirecting || currentIndex === 0}
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition active:scale-[0.97] ${
              submitting || redirecting || currentIndex === 0
                ? "cursor-not-allowed opacity-35"
                : "hover:bg-white/14"
            }`}
            aria-label={t("required_actions.previous_action")}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <p className="flex h-11 min-w-[108px] items-center justify-center rounded-full bg-white/10 px-5 text-center text-base font-semibold text-white/82">
            {t("required_actions.progress", { current: progressCurrent, total })}
          </p>
          <button
            type="button"
            onClick={handleNextAction}
            disabled={submitting || redirecting || currentIndex >= total - 1}
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition active:scale-[0.97] ${
              submitting || redirecting || currentIndex >= total - 1
                ? "cursor-not-allowed opacity-35"
                : "hover:bg-white/14"
            }`}
            aria-label={t("required_actions.next_action")}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
              <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      ) : null}

      {!isModerationWarning && !isPremiumMonthlyBenefits && !isHostAttentionAction ? (
        <h3 className="text-xl font-bold leading-tight text-white">{displayedHeading}</h3>
      ) : null}

      {shouldShowEventCard ? <div className="mt-4">{eventCard}</div> : null}

      <div className={`${isModerationWarning ? "mt-1" : "mt-4"} space-y-4`}>
        {displayedBodyParagraphs.map((paragraph, index) => (
          <p key={`${current.id}-${index}`} className="text-sm leading-relaxed text-white/85">
            {paragraph}
          </p>
        ))}
      </div>

      {showHostPaidIntentPaymentNotice ? (
        <HostPaidCancellationNotice
          t={t}
          policy={hostPaidCancellationPolicy}
          variant="remaining"
        />
      ) : null}

      {participantReviewCard}

      {current.type === "host_success_reward" && hostRewardItems.length > 0 ? (
        <div className="mt-4 rounded-xl border border-white/20 bg-white/5 px-3 py-3">
          <p className="text-center text-base font-bold text-white">
            {t("participants_section.host_reward_title")}
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-center">
            {hostRewardItems.map((item) => (
              item.kind === "coins" ? (
                <div key={item.key} className="flex items-center gap-1.5 text-white">
                  <span className="text-xl font-extrabold leading-none">{item.amountLabel}</span>
                  <img src={item.icon} alt={item.label} className={item.iconClassName} />
                </div>
              ) : (
                <div key={item.key} className="flex items-center gap-1.5 text-white">
                  <img src={item.icon} alt={item.label} className={item.iconClassName} />
                  <span className="-ml-1 text-xl font-extrabold leading-none">× {item.amountLabel}</span>
                </div>
              )
            ))}
          </div>
        </div>
      ) : null}

      <style>{`
        @keyframes vgRewardFly {
          0% {
            transform: translate(-50%, -50%) scale(0.5);
            opacity: 0;
          }
          18% {
            opacity: 1;
          }
          100% {
            transform: translate(calc(-50% + var(--vg-reward-tx)), calc(-50% + var(--vg-reward-ty))) scale(0.95);
            opacity: 0;
          }
        }
      `}</style>
      </AppModalShell>
      <ConceptDatePollModal
        visible={conceptDatePollOpen}
        eventId={eventData?.id}
        durationMinutes={eventData?.approximate_duration}
        onClose={() => setConceptDatePollOpen(false)}
        onSaved={handleConceptDatePollSaved}
        zIndexClassName="z-[13030]"
      />
    </>
  );
};

export default RequiredActionsModal;
